import dayjs, { Dayjs } from 'dayjs';
import {
  GanttTask,
  FlattenedTask,
  TimelineConfig,
  ZoomLevel,
  ROW_HEIGHT,
  BAR_HEIGHT,
  BAR_PADDING,
  MIN_BAR_WIDTH,
  WEEKEND_SHADING_MIN_DAY_WIDTH,
} from '../types/gantt.types';

/**
 * Convert a date to x-pixel position on the timeline
 */
export function dateToPixel(
  date: string | Dayjs,
  timelineStart: Dayjs,
  dayWidth: number
): number {
  const targetDate = dayjs(date);
  const diffDays = targetDate.diff(timelineStart, 'day', true);
  return Math.round(diffDays * dayWidth);
}

/**
 * Convert x-pixel position to date
 */
export function pixelToDate(
  x: number,
  timelineStart: Dayjs,
  dayWidth: number
): Dayjs {
  const days = x / dayWidth;
  return timelineStart.add(days, 'day');
}

/**
 * Calculate task bar position and dimensions
 */
export function calculateTaskBarPosition(
  task: FlattenedTask,
  config: TimelineConfig
): { x: number; y: number; width: number; height: number } {
  const startX = dateToPixel(task.startDate, config.startDate, config.dayWidth);
  const endX = dateToPixel(task.endDate, config.startDate, config.dayWidth);

  return {
    x: startX,
    y: task.rowIndex * config.rowHeight + BAR_PADDING,
    // A single-day task is one dayWidth wide — but past the coarse zoom levels
    // that rounds to nothing, so hold it at a visible sliver.
    width: Math.max(endX - startX, config.dayWidth, MIN_BAR_WIDTH),
    height: BAR_HEIGHT,
  };
}

/**
 * Calculate timeline range with buffer from tasks
 */
export function calculateTimelineRange(
  tasks: GanttTask[],
  bufferDays: number = 7
): { start: Dayjs; end: Dayjs; totalDays: number } {
  let minDate: Dayjs = dayjs().add(1, 'year');
  let maxDate: Dayjs = dayjs().subtract(1, 'year');
  let hasValidDates = false;

  const processTask = (task: GanttTask) => {
    if (task.startDate && task.endDate) {
      const start = dayjs(task.startDate);
      const end = dayjs(task.endDate);

      if (start.isValid() && end.isValid()) {
        hasValidDates = true;
        if (start.isBefore(minDate)) minDate = start;
        if (end.isAfter(maxDate)) maxDate = end;
      }
    }

    task.children?.forEach(processTask);
  };

  tasks.forEach(processTask);

  if (!hasValidDates) {
    minDate = dayjs().subtract(1, 'month');
    maxDate = dayjs().add(3, 'month');
  }

  const startDate = minDate.subtract(bufferDays, 'day').startOf('week');
  const endDate = maxDate.add(bufferDays, 'day').endOf('week');
  const totalDays = endDate.diff(startDate, 'day');

  return { start: startDate, end: endDate, totalDays };
}

// ==================== Timescale ====================

/**
 * Units either header row can step in. `decade` only ever appears on the top
 * row, above a row of years.
 */
type TimescaleUnit = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'decade';

/**
 * P6-style pairing: the bottom row steps in the unit you picked, the top row
 * in the next larger one, so there is always a coarser label to orient by.
 */
const TIMESCALE_ROWS: Record<ZoomLevel, { top: TimescaleUnit; bottom: TimescaleUnit }> = {
  day: { top: 'month', bottom: 'day' },
  week: { top: 'month', bottom: 'week' },
  month: { top: 'year', bottom: 'month' },
  quarter: { top: 'year', bottom: 'quarter' },
  year: { top: 'decade', bottom: 'year' },
};

function startOfUnit(date: Dayjs, unit: TimescaleUnit): Dayjs {
  switch (unit) {
    case 'day':
      return date.startOf('day');
    case 'week':
      return date.startOf('week');
    case 'month':
      return date.startOf('month');
    // Derived rather than via dayjs' quarterOfYear plugin, which this app
    // doesn't register.
    case 'quarter':
      return date.startOf('month').subtract(date.month() % 3, 'month');
    case 'year':
      return date.startOf('year');
    case 'decade':
      return date.startOf('year').subtract(date.year() % 10, 'year');
  }
}

function endOfUnit(unitStart: Dayjs, unit: TimescaleUnit): Dayjs {
  switch (unit) {
    case 'day':
      return unitStart.add(1, 'day');
    case 'week':
      return unitStart.add(1, 'week');
    case 'month':
      return unitStart.add(1, 'month');
    case 'quarter':
      return unitStart.add(3, 'month');
    case 'year':
      return unitStart.add(1, 'year');
    case 'decade':
      return unitStart.add(10, 'year');
  }
}

function unitLabel(date: Dayjs, unit: TimescaleUnit): string {
  switch (unit) {
    // Day and week rows both sit under a month row, so the day-of-month reads
    // unambiguously on its own — a week column is labelled by its first day.
    case 'day':
    case 'week':
      return date.format('DD');
    case 'month':
      return date.format('MMM');
    case 'quarter':
      return `Q${Math.floor(date.month() / 3) + 1}`;
    case 'year':
      return date.format('YYYY');
    case 'decade':
      return `${date.year() - (date.year() % 10)}s`;
  }
}

/** Top-row labels carry the year, since the bottom row rarely does. */
function topRowLabel(date: Dayjs, unit: TimescaleUnit): string {
  return unit === 'month' ? date.format('MMM YYYY') : unitLabel(date, unit);
}

export interface DateHeader {
  date: Dayjs;
  label: string;
  /** Pixel offset of the cell's left edge from the timeline start. */
  x: number;
  width: number;
  isWeekend: boolean;
  isToday: boolean;
}

/**
 * Walk the timeline one `unit` at a time, clipping the first and last cells to
 * the timeline bounds so a partial month/quarter/year renders at its true
 * width and the row still sums to exactly `totalDays * dayWidth`.
 */
function buildTimescaleRow(
  config: TimelineConfig,
  unit: TimescaleUnit,
  isTopRow: boolean
): DateHeader[] {
  const cells: DateHeader[] = [];
  if (config.totalDays <= 0) return cells;

  const timelineStart = config.startDate.startOf('day');
  const timelineEnd = timelineStart.add(config.totalDays, 'day'); // exclusive
  const today = dayjs().startOf('day');

  let cursor = timelineStart;
  let x = 0;

  // Runaway guard: a decades-long project at day zoom would otherwise try to
  // mount a cell per day.
  let guard = 0;
  while (cursor.isBefore(timelineEnd) && guard++ < 10000) {
    const boundary = endOfUnit(startOfUnit(cursor, unit), unit);
    const cellEnd = boundary.isAfter(timelineEnd) ? timelineEnd : boundary;
    const days = cellEnd.diff(cursor, 'day');
    if (days <= 0) break;

    const width = days * config.dayWidth;
    cells.push({
      date: cursor,
      label: isTopRow ? topRowLabel(cursor, unit) : unitLabel(cursor, unit),
      x,
      width,
      isWeekend: unit === 'day' && (cursor.day() === 0 || cursor.day() === 6),
      isToday: !today.isBefore(cursor) && today.isBefore(cellEnd),
    });

    x += width;
    cursor = cellEnd;
  }

  return cells;
}

/**
 * Build both header rows for the current zoom level.
 */
export function generateDateHeaders(
  config: TimelineConfig
): { primary: DateHeader[]; secondary: DateHeader[] } {
  const { top, bottom } = TIMESCALE_ROWS[config.zoomLevel];

  return {
    primary: buildTimescaleRow(config, top, true),
    secondary: buildTimescaleRow(config, bottom, false),
  };
}

/**
 * X positions of the timeline's vertical grid lines — one per bottom-row cell,
 * so the grid always tracks the visible timescale instead of drawing a line
 * per day at every zoom level.
 */
export function getGridLinePositions(config: TimelineConfig): number[] {
  const { bottom } = TIMESCALE_ROWS[config.zoomLevel];
  return buildTimescaleRow(config, bottom, false).map((cell) => cell.x);
}

/**
 * Weekend bands, but only while a day is wide enough for them to read as
 * bands rather than noise. Returns [] at month zoom and coarser.
 */
export function getWeekendBands(
  config: TimelineConfig
): { x: number; width: number }[] {
  if (config.dayWidth < WEEKEND_SHADING_MIN_DAY_WIDTH) return [];

  return buildTimescaleRow(config, 'day', false)
    .filter((cell) => cell.isWeekend)
    .map((cell) => ({ x: cell.x, width: cell.width }));
}

/**
 * Format date for display
 */
export function formatDate(date: string | Dayjs, format: string = 'DD-MMM-YYYY'): string {
  const d = dayjs(date);
  return d.isValid() ? d.format(format) : '--';
}

/**
 * Calculate duration between two dates in days
 */
export function calculateDuration(startDate: string, endDate: string): number {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
}

/**
 * Check if a date is a weekend
 */
export function isWeekend(date: Dayjs): boolean {
  const day = date.day();
  return day === 0 || day === 6;
}

/**
 * Get today's x position on the timeline
 */
export function getTodayPosition(config: TimelineConfig): number | null {
  const today = dayjs().startOf('day');

  if (today.isBefore(config.startDate) || today.isAfter(config.endDate)) {
    return null;
  }

  return dateToPixel(today, config.startDate, config.dayWidth);
}
